import React from 'react';
import { ChevronDown } from 'lucide-react';
import { IntegrationLogo, GIGA_MARK } from '../IntegrationLogo';
import { EXPORT_BODY, EXPORT_META } from './exportUi';

/**
 * GIGA ANSWERING INSIDE CHATGPT — AN EXPORT-ONLY ASSET.
 *
 * WHY NOT `ChatGPTPanel`. That component rebuilds the whole ChatGPT
 * application: sidebar, recents list, account row, model dropdown, composer,
 * and a four-turn transcript. On the page it earns that, because the point
 * there is "this is really ChatGPT". As a downloadable asset it fails, and for
 * a specific reason: at the size an asset is actually viewed — a card on a
 * slide, an overlay on a phone video — the sidebar and composer eat half the
 * frame, the transcript runs past what anyone will read, and nothing in it is
 * legible enough to make the one point that matters.
 *
 * That point is narrow: ChatGPT asked Giga, Giga went to a real system, and
 * the answer came back with the record it came from. So this frame is exactly
 * that, at a size you can read from across a room:
 *
 *   · Enough ChatGPT chrome to be recognisably ChatGPT — the mark and the
 *     model name — and no more. No sidebar, no composer, no account row.
 *   · ONE question, in the user's own bubble.
 *   · The Giga call, made visible as a step rather than hidden: the mark, the
 *     system it reached, and what it took. This is the whole asset; it is the
 *     only element that gets the accent.
 *   · The answer, with the source named underneath.
 *
 * Static by design, like every frame on the export page.
 */

/** The three records behind the answer. Named, because "from your CRM" is a
 * claim and "Northwind · Q3 renewal" is a fact. */
const SOURCES: [string, string][] = [['salesforce', 'Salesforce'], ['gmail', 'Gmail']];
export function GigaInChatGPT({
  /** Phone-width artboards drop the model row's right side and tighten the
   * gutters — the transcript itself is already a single column. */
  portrait = false
}: {
  portrait?: boolean;
} = {}) {
  return <div className={`w-full overflow-hidden rounded-[20px] border border-[#E5E5E5] bg-white ${portrait ? '' : ''} shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_40px_-18px_rgba(43,33,26,0.22)]`} data-id="element-3229" data-mp="u01ugng2zsnl">
      {/* ChatGPT's own chrome, reduced to the two things that identify it. */}
      <header className="flex items-center justify-between border-b border-[#F0F0F0] px-5 py-3.5" data-id="element-3230" data-mp="yzwhgfwqjy3d">
        <span className="flex items-center gap-2.5" data-id="element-3231" data-mp="1dokbuiuui1rq">
          <IntegrationLogo slug="openai" name="ChatGPT" className="size-[19px]" rounded="rounded-full" data-id="element-3232" data-mp="1dn99mnh1t22q" />
          <span className="text-[15px] font-medium text-[#0D0D0D]" data-id="element-3233" data-mp="18pg3fufqw82g">
            ChatGPT <span className="text-[#8F8F8F]" data-id="element-3234" data-mp="1mofoyt7yo6ue">5</span>
          </span>
          <ChevronDown size={14} className="text-[#8F8F8F]" data-id="element-3235" data-mp="1hqnrzksglujc" />
        </span>
        {!portrait && <span className="rounded-full border border-[#E5E5E5] px-2.5 py-1 text-[11.5px] font-semibold text-[#5D5D5D]" data-id="element-3236" data-mp="3oglu00edu5zm">
            Giga connected
          </span>}
      </header>

      <div className={`flex flex-col gap-5 ${portrait ? 'px-4 py-5' : 'px-6 py-6'}`} data-id="element-3237" data-mp="3jk56n8f0bvnl">
        {/* The question, in ChatGPT's own bubble treatment. */}
        <p className="max-w-[85%] self-end rounded-[20px] bg-[#F4F4F4] px-4 py-2.5 text-[15px] leading-[1.6] text-[#0D0D0D]" data-id="element-3238" data-mp="23xrrcmnl9jhs">
          Which renewals are at risk this quarter, and why?
        </p>

        {/* THE ASSET. The call out to Giga, shown as a step that happened —
         this is the only element carrying the accent, because it is the only
         thing this frame exists to say. */}
        <div className="rounded-[16px] border border-[#EFE7DA] bg-[#FAF8F4] px-4 py-3.5" data-id="element-3239" data-mp="1l9d1i2p0cbik">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3240" data-mp="179c3cv04ni6s">
            <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[17px] flex-none object-contain" data-id="element-3241" data-mp="17awtm8feseyu" />
            <span className="text-[13px] font-bold text-[#2B211A]" data-id="element-3242" data-mp="31muunn3ke4jn">Giga</span>
            <span className="text-[#D8CFC2]" data-id="element-3243" data-mp="31kh51trnlkjh">·</span>
            <span className={EXPORT_META} data-id="element-3244" data-mp="35n5arsl2mbc8">Reading your systems</span>
            <span className="ml-auto flex items-center gap-1.5" data-id="element-3245" data-mp="2gx93sxreme1x">
              {SOURCES.map(([slug, name]) => <span key={slug} className="flex size-[26px] items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-3246" data-mp="2bypeqohikm5c">
                  <IntegrationLogo slug={slug} name={name} className="size-[14px]" rounded="rounded" data-id="element-3247" data-mp="26y1q29grchea" />
                </span>)}
            </span>
          </div>
          <p className={`${EXPORT_BODY} mt-2.5`} data-id="element-3248" data-mp="1xyohdj0gjwum">
            18 open renewals · 3 flagged · 12 email threads read
          </p>
        </div>

        {/* The answer, unbubbled — the way an assistant reply actually reads in
         ChatGPT — with the record named so the claim is checkable. */}
        <div data-id="element-3249" data-mp="1j9sbnslvivx6">
          <p className="text-[15px] leading-[1.7] text-[#0D0D0D]" data-id="element-3250" data-mp="1jaadr5v7w4d0">
            Three are at risk. <strong className="font-semibold" data-id="element-3251" data-mp="1eafnn4j9nit0">Northwind Systems</strong>{' '}
            ($84k, closes 22 Sep) has had no executive contact in 46 days.{' '}
            <strong className="font-semibold" data-id="element-3252" data-mp="1a9lwm2aj1nvw">Meridian Health</strong> ($61k) lost its
            champion in July with no replacement named.{' '}
            <strong className="font-semibold" data-id="element-3253" data-mp="3pxr89d6bbyh8">Fairline Group</strong> ($37k) is down 38% in
            usage month over month.
          </p>
          <p className={`${EXPORT_META} mt-3 flex flex-wrap items-center gap-x-2 gap-y-1`} data-id="element-3254" data-mp="3uyf7un8lxnn2">
            <span className="font-semibold text-[#2B211A]" data-id="element-3255" data-mp="3phvqhhhzp8r">Sources</span>
            <span className="text-[#D8CFC2]" data-id="element-3256" data-mp="328yixzk74ovl">·</span>
            Salesforce opportunities, 3 records
            <span className="text-[#D8CFC2]" data-id="element-3257" data-mp="37702smpg4s84">·</span>
            Gmail threads, 12 read
          </p>
        </div>
      </div>
    </div>;
}